import React from 'react';
import { ArrowLeft, HelpCircle, Euro, Syringe, Clock, FileQuestion } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

const FAQCard = ({ title, icon: Icon, link }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      onClick={() => navigate(link)}
      className="p-6 cursor-pointer group hover:shadow-xl transition-all duration-300 border border-gray-100 rounded-none bg-ivory"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-gold/10 rounded-none flex items-center justify-center group-hover:bg-gold transition-colors">
          <Icon className="text-gold group-hover:text-white transition-colors" size={28} />
        </div>
        <h4 className="text-lg font-medium text-[#1C1C1A] group-hover:text-gold transition-colors flex-1">
          {title}
        </h4>
      </div>
    </Card>
  );
};

const FAQ = () => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      back: 'Volver a Rinomodelación',
      title: 'Preguntas Frecuentes',
      subtitle: 'Resolvemos todas tus dudas sobre la rinomodelación',
      detailedTitle: 'Preguntas Detalladas',
      detailedSubtitle: 'Haz clic en cualquier pregunta para ver la respuesta completa',
      quickTitle: 'Respuestas Rápidas',
      moreQuestions: '¿Tienes más preguntas?',
      moreQuestionsDesc: 'Contacta directamente con el equipo del Dr. Norro para resolver cualquier duda.',
      contactUs: 'Contactar',
      faqs: [
        { title: '¿Cómo es el procedimiento de Rinomodelación?', link: '/rinomodelacion/faq/procedimiento' },
        { title: '¿En qué consiste el estudio de imagen?', link: '/rinomodelacion/faq/estudio-imagen' },
        { title: '¿Cuánto cuesta la Rinomodelación?', link: '/rinomodelacion/faq/precios' },
        { title: '¿Qué materiales y técnica usa el Dr. Norro?', link: '/rinomodelacion/faq/materiales' },
        { title: '¿Cuál es la diferencia entre Rinomodelación y Rinoplastia?', link: '/rinomodelacion/faq/diferencias' },
        { title: '¿Los resultados son permanentes?', link: '/rinomodelacion/faq/duracion' },
      ],
      quickFaqs: [
        { question: '¿Duele el procedimiento?', answer: 'Es prácticamente indoloro. Se aplica anestesia local tópica que minimiza cualquier molestia durante el tratamiento.' },
        { question: '¿Cuánto dura el procedimiento?', answer: 'Entre 15 y 30 minutos dependiendo de la complejidad del caso.' },
        { question: '¿Hay tiempo de recuperación?', answer: 'No. Puedes volver a tu vida normal inmediatamente después del tratamiento.' },
        { question: '¿Quedan marcas o cicatrices?', answer: 'No. Al no haber cirugía, no quedan cicatrices ni marcas visibles.' },
      ],
    },
    en: {
      back: 'Back to Rhinomodeling',
      title: 'Frequently Asked Questions',
      subtitle: 'We answer all your questions about rhinomodeling',
      detailedTitle: 'Detailed Questions',
      detailedSubtitle: 'Click on any question to see the full answer',
      quickTitle: 'Quick Answers',
      moreQuestions: 'Do you have more questions?',
      moreQuestionsDesc: 'Contact Dr. Norro\'s team directly to resolve any doubts.',
      contactUs: 'Contact Us',
      faqs: [
        { title: 'How is the Rhinomodeling procedure?', link: '/rinomodelacion/faq/procedimiento' },
        { title: 'What does the image study consist of?', link: '/rinomodelacion/faq/estudio-imagen' },
        { title: 'How much does Rhinomodeling cost?', link: '/rinomodelacion/faq/precios' },
        { title: 'What materials and technique does Dr. Norro use?', link: '/rinomodelacion/faq/materiales' },
        { title: 'What is the difference between Rhinomodeling and Rhinoplasty?', link: '/rinomodelacion/faq/diferencias' },
        { title: 'Are the results permanent?', link: '/rinomodelacion/faq/duracion' },
      ],
      quickFaqs: [
        { question: 'Is the procedure painful?', answer: 'It is practically painless. Topical local anesthesia is applied to minimize any discomfort during treatment.' },
        { question: 'How long does the procedure take?', answer: 'Between 15 and 30 minutes depending on the complexity of the case.' },
        { question: 'Is there recovery time?', answer: 'No. You can return to your normal life immediately after treatment.' },
        { question: 'Are there marks or scars?', answer: 'No. Since there is no surgery, there are no visible scars or marks.' },
      ],
    },
    fr: {
      back: 'Retour au Rhinomodelage',
      title: 'Questions Fréquentes',
      subtitle: 'Nous répondons à toutes vos questions sur le rhinomodelage',
      detailedTitle: 'Questions Détaillées',
      detailedSubtitle: 'Cliquez sur n\'importe quelle question pour voir la réponse complète',
      quickTitle: 'Réponses Rapides',
      moreQuestions: 'Avez-vous d\'autres questions?',
      moreQuestionsDesc: 'Contactez directement l\'équipe du Dr. Norro pour résoudre tout doute.',
      contactUs: 'Nous Contacter',
      faqs: [
        { title: 'Comment se déroule la procédure de Rhinomodelage?', link: '/rinomodelacion/faq/procedimiento' },
        { title: 'En quoi consiste l\'étude d\'image?', link: '/rinomodelacion/faq/estudio-imagen' },
        { title: 'Combien coûte le Rhinomodelage?', link: '/rinomodelacion/faq/precios' },
        { title: 'Quels matériaux et technique le Dr. Norro utilise-t-il?', link: '/rinomodelacion/faq/materiales' },
        { title: 'Quelle est la différence entre Rhinomodelage et Rhinoplastie?', link: '/rinomodelacion/faq/diferencias' },
        { title: 'Les résultats sont-ils permanents?', link: '/rinomodelacion/faq/duracion' },
      ],
      quickFaqs: [
        { question: 'La procédure est-elle douloureuse?', answer: 'Elle est pratiquement indolore. Une anesthésie locale topique est appliquée pour minimiser tout inconfort pendant le traitement.' },
        { question: 'Combien de temps dure la procédure?', answer: 'Entre 15 et 30 minutes selon la complexité du cas.' },
        { question: 'Y a-t-il un temps de récupération?', answer: 'Non. Vous pouvez reprendre votre vie normale immédiatement après le traitement.' },
        { question: 'Y a-t-il des marques ou des cicatrices?', answer: 'Non. Comme il n\'y a pas de chirurgie, il n\'y a pas de cicatrices ni de marques visibles.' },
      ],
    },
    pt: {
      back: 'Voltar para Rinomodelação',
      title: 'Perguntas Frequentes',
      subtitle: 'Respondemos todas as suas dúvidas sobre rinomodelação',
      detailedTitle: 'Perguntas Detalhadas',
      detailedSubtitle: 'Clique em qualquer pergunta para ver a resposta completa',
      quickTitle: 'Respostas Rápidas',
      moreQuestions: 'Tem mais perguntas?',
      moreQuestionsDesc: 'Entre em contato diretamente com a equipe do Dr. Norro para resolver qualquer dúvida.',
      contactUs: 'Contate-nos',
      faqs: [
        { title: 'Como é o procedimento de Rinomodelação?', link: '/rinomodelacion/faq/procedimiento' },
        { title: 'Em que consiste o estudo de imagem?', link: '/rinomodelacion/faq/estudio-imagen' },
        { title: 'Quanto custa a Rinomodelação?', link: '/rinomodelacion/faq/precios' },
        { title: 'Quais materiais e técnica o Dr. Norro usa?', link: '/rinomodelacion/faq/materiales' },
        { title: 'Qual é a diferença entre Rinomodelação e Rinoplastia?', link: '/rinomodelacion/faq/diferencias' },
        { title: 'Os resultados são permanentes?', link: '/rinomodelacion/faq/duracion' },
      ],
      quickFaqs: [
        { question: 'O procedimento dói?', answer: 'É praticamente indolor. É aplicada anestesia local tópica que minimiza qualquer desconforto durante o tratamento.' },
        { question: 'Quanto tempo dura o procedimento?', answer: 'Entre 15 e 30 minutos dependendo da complexidade do caso.' },
        { question: 'Há tempo de recuperação?', answer: 'Não. Você pode voltar à sua vida normal imediatamente após o tratamento.' },
        { question: 'Ficam marcas ou cicatrizes?', answer: 'Não. Como não há cirurgia, não ficam cicatrices nem marcas visíveis.' },
      ],
    },
  };

  const t = texts[lang] || texts.es;

  const faqIcons = [Syringe, FileQuestion, Euro, Syringe, HelpCircle, Clock];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.back}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* FAQ Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-2xl font-light text-[#1C1C1A] mb-4">{t.detailedTitle}</h2>
          <p className="text-text-secondary mb-8">{t.detailedSubtitle}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.faqs.map((faq, index) => (
              <FAQCard 
                key={index}
                title={faq.title}
                icon={faqIcons[index]}
                link={faq.link}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Quick FAQs */}
      <section className="py-16 px-4 bg-ivory">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-light text-[#1C1C1A] mb-8 text-center">{t.quickTitle}</h2>
          
          <div className="space-y-6">
            {t.quickFaqs.map((faq, index) => (
              <div key={index} className="bg-cream p-6 rounded-none">
                <h4 className="font-semibold text-[#1C1C1A] mb-2">{faq.question}</h4>
                <p className="text-text-secondary">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-light text-[#1C1C1A] mb-4">{t.moreQuestions}</h2>
          <p className="text-text-secondary mb-8">
            {t.moreQuestionsDesc}
          </p>
          <Button 
            onClick={() => navigate('/rinomodelacion/contacto')}
            className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
          >
            {t.contactUs}
          </Button>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default FAQ;
